/**
 * PhaseTimingBuffer — fixed-capacity ring buffer for per-frame phase timings.
 *
 * The latency-attribution arc captures wall-time deltas at two client-side
 * sites (input handler / predict+render). Each tick worth of timings becomes
 * one `PhaseTimingSample`; the buffer keeps the last N for the in-game Latency
 * tab to render sparklines and rolling averages.
 *
 * Phase 6 (reconcile) removed 2026-05-27 — client-owned movement has no
 * reconciliation step.
 *
 * All times in milliseconds (sub-ms precision via `performance.now()`).
 * Pure module — no DOM/network deps; one instance per game session.
 */

const DEFAULT_CAPACITY = 256;

export interface PhaseTimingSample {
  /** `performance.now()` when this sample was recorded (frame end). */
  recordedAt: number;
  /**
   * Phase 1 — input handler latency (ms). Time from raw browser keydown
   * (`KeyboardEvent.timeStamp`) to the moment InputSystem hands the
   * command off to the next render-frame's generateCommand() read.
   * `null` when no input was processed this frame.
   */
  phase1Ms: number | null;
  /**
   * Phase 2 — client predict + render budget (ms). Wall time inside the
   * EntitySystem accumulator loop (physics tick + sprite sync). Sums all
   * sub-ticks dispatched in this render frame.
   */
  phase2Ms: number;
}

export class PhaseTimingBuffer {
  private samples: PhaseTimingSample[] = [];
  private readonly capacity: number;

  constructor(capacity: number = DEFAULT_CAPACITY) {
    this.capacity = capacity;
  }

  push(sample: PhaseTimingSample): void {
    this.samples.push(sample);
    if (this.samples.length > this.capacity) this.samples.shift();
  }

  size(): number {
    return this.samples.length;
  }

  /** Most recent sample, or null if buffer is empty. */
  latest(): PhaseTimingSample | null {
    return this.samples.length === 0 ? null : this.samples[this.samples.length - 1];
  }

  /**
   * Return all samples, oldest first. Caller MUST treat as read-only —
   * mutating the returned array breaks subsequent push semantics.
   */
  snapshot(): readonly PhaseTimingSample[] {
    return this.samples;
  }

  /**
   * Rolling-window mean of a single phase across the last `windowSize`
   * samples. `null` entries are skipped (so a phase that wasn't
   * exercised this frame doesn't pull the mean toward 0). Returns 0 if
   * no non-null samples in the window.
   */
  rollingMean(phase: 'phase1Ms' | 'phase2Ms', windowSize: number = 60): number {
    if (this.samples.length === 0) return 0;
    const start = Math.max(0, this.samples.length - windowSize);
    let sum = 0;
    let count = 0;
    for (let i = start; i < this.samples.length; i++) {
      const v = this.samples[i][phase];
      if (typeof v === 'number' && Number.isFinite(v)) {
        sum += v;
        count++;
      }
    }
    return count === 0 ? 0 : sum / count;
  }

  /**
   * Rolling-window max for the same phase. Useful for surfacing spikes
   * that the mean would smooth away.
   */
  rollingMax(phase: 'phase1Ms' | 'phase2Ms', windowSize: number = 60): number {
    if (this.samples.length === 0) return 0;
    const start = Math.max(0, this.samples.length - windowSize);
    let max = 0;
    for (let i = start; i < this.samples.length; i++) {
      const v = this.samples[i][phase];
      if (typeof v === 'number' && Number.isFinite(v) && v > max) max = v;
    }
    return max;
  }

  reset(): void {
    this.samples = [];
  }
}
